/**
 * What a booking accepts as the customer's phone number and email address.
 *
 * The browser's own checks are too generous for the one form a stranger fills
 * in: `type="tel"` checks nothing at all, so "177" went through as a phone
 * number, and `type="email"` takes "ana@gmail" — an address with no domain
 * ending, which no mail ever reaches. The emailed link is the only way an
 * anonymous customer can change or cancel a booking, and the phone is how the
 * business reaches them, so both have to be real enough to use.
 *
 * A shape check, not a directory lookup:
 * - phone: 8 to 15 digits (15 is the international maximum; 8 still refuses
 *   "177" or "112" without refusing countries whose numbers are shorter than
 *   Romania's 10), an optional leading "+", and spaces, dots, dashes, slashes or
 *   brackets between them as people write them;
 * - email: something@domain.tld, where the ending is letters (or a punycode
 *   "xn--" one), so "ana@gmail", "ana@gmail.c" and "ana@gmail,com" are refused.
 *
 * The patterns are written for the `pattern` attribute, which current browsers
 * compile with the `v` flag (stricter escaping inside […]) and older ones with
 * `u`; they are valid under both. An invalid pattern is silently ignored by the
 * browser, so contactFormat.test.js compiles them both ways.
 *
 * The API enforces the same two patterns (`ContactFormat` in
 * EasyPlanner.Common/Helpers). Change one, change the other.
 */

/** The column lengths behind PublicBookingRequest: longer is refused by the API. */
export const PHONE_MAX_LENGTH = 20;
export const EMAIL_MAX_LENGTH = 255;

export const PHONE_PATTERN = String.raw`^(?=(?:[^0-9]*[0-9]){8,15}[^0-9]*$)\s*\+?[0-9\s\(\)\.\/\-]+$`;

export const EMAIL_PATTERN = String.raw`^[^\s@]+@(?:[\p{L}\p{N}\-]+\.)+(?:\p{L}{2,}|xn--[\p{L}\p{N}\-]+)$`;

const PHONE = new RegExp(PHONE_PATTERN, "u");
const EMAIL = new RegExp(EMAIL_PATTERN, "u");

/** Whether `value` is a phone number the booking form would accept. */
export const isValidPhone = (value) => PHONE.test(String(value ?? "").trim());

/** Whether `value` is an email address the booking form would accept. */
export const isValidEmail = (value) => EMAIL.test(String(value ?? "").trim());
